import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteHeader from '../../components/feature/SiteHeader';
import SiteFooter from '../../components/feature/SiteFooter';
import ArticleCard from '../../components/feature/ArticleCard';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { getCategories, searchArticles } from '../../lib/content';

type SortKey = 'latest' | 'hot';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const categoryParam = searchParams.get('category') || 'all';
  const sortParam = (searchParams.get('sort') as SortKey) || 'latest';

  const [keyword, setKeyword] = useState(q);

  useEffect(() => {
    setKeyword(q);
  }, [q]);

  const categories = getCategories();

  const results = useMemo(
    () => searchArticles(q, { category: categoryParam, sort: sortParam }),
    [q, categoryParam, sortParam],
  );

  const updateParams = (patch: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    setSearchParams(next);
  };

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    updateParams({ q: keyword.trim() });
  };

  const suggestKeywords = ['城市更新', '人工智能', '养生', '国潮', '新能源'];

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1 w-full px-4 md:px-8 py-6">
        <div className="max-w-5xl mx-auto">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索结果' }]} />

          <div className="mt-5">
            <h1 className="font-heading font-bold text-2xl text-foreground-950">
              站内搜索
            </h1>
            <p className="mt-1 text-sm text-foreground-500">
              输入关键词，检索全站资讯、话题与专题内容（前端演示过滤）
            </p>
          </div>

          <form onSubmit={handleSearch} className="mt-5">
            <div className="flex items-center gap-2 bg-background-50 rounded-lg border border-background-200 p-1.5 pl-4 focus-within:border-primary-300 focus-within:ring-2 focus-within:ring-primary-100 transition-all">
              <i className="ri-search-line text-foreground-400" />
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索资讯、话题、标签..."
                className="flex-1 bg-transparent h-10 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
              <button
                type="submit"
                className="h-10 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </div>
          </form>

          {/* 筛选条件 */}
          <div className="mt-4 flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500 mr-1">类别：</span>
              <button
                type="button"
                onClick={() => updateParams({ category: '' })}
                className={`px-3 py-1 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  categoryParam === 'all'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:text-primary-600'
                }`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => updateParams({ category: cat.key })}
                  className={`px-3 py-1 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    categoryParam === cat.key
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:text-primary-600'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500 mr-1">排序：</span>
              <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-100">
                {[
                  { key: 'latest' as SortKey, label: '最新' },
                  { key: 'hot' as SortKey, label: '最热' },
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => updateParams({ sort: item.key })}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      sortParam === item.key
                        ? 'bg-accent-500 text-white'
                        : 'text-foreground-600 hover:text-accent-600'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 结果统计 */}
          <div className="mt-6 pb-4 border-b border-background-200">
            {q ? (
              <p className="text-sm text-foreground-600">
                关于 “<span className="text-primary-600 font-medium">{q}</span>” ，
                找到约 <strong className="text-primary-600">{results.length}</strong> 条相关内容
              </p>
            ) : (
              <p className="text-sm text-foreground-600">
                输入关键词开始检索，或浏览下方热门推荐
              </p>
            )}
          </div>

          {/* 结果列表 */}
          <div className="mt-6">
            {results.length > 0 ? (
              <div className="rounded-lg border border-background-200 bg-background-50 px-4 md:px-5">
                {results.map((article) => (
                  <ArticleCard key={article.id} article={article} variant="list" />
                ))}
              </div>
            ) : (
              <div className="py-16 text-center rounded-lg border border-background-200 bg-background-100">
                <i className="ri-search-eye-line text-4xl text-foreground-300" />
                <p className="mt-4 text-foreground-700 font-medium">没有找到匹配的内容</p>
                <p className="mt-1 text-sm text-foreground-500">
                  试试更换关键词，或调整筛选条件
                </p>
                <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                  <span className="text-sm text-foreground-500">试试：</span>
                  {suggestKeywords.map((kw) => (
                    <Link
                      key={kw}
                      to={`/search?q=${encodeURIComponent(kw)}`}
                      className="px-3 py-1 rounded-full bg-background-50 border border-background-200 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors"
                    >
                      {kw}
                    </Link>
                  ))}
                </div>
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-home-line" />
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}